import { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { getOperatorsForType } from '../utils/applyDataPipeline';

function FilterRuleRow({ rule, columns, onChange, onRemove, ui }) {
  const col = columns.find(c => c.name === rule.column);
  const operators = getOperatorsForType(col?.type || 'string');
  const hideValue = rule.operator === 'is_empty' || rule.operator === 'is_not_empty';
  const isBetween = rule.operator === 'between';

  return (
    <div className="flex items-center gap-2 text-xs">
      <button
        onClick={() => onChange({ ...rule, enabled: !rule.enabled })}
        className="flex-shrink-0 w-5 h-5 flex items-center justify-center border rounded"
        style={{
          borderColor: rule.enabled ? ui.accent : ui.borderSecondary,
          backgroundColor: rule.enabled ? ui.accent : 'transparent',
          color: rule.enabled ? ui.accentFg : ui.textSecondary,
        }}
        title={rule.enabled ? 'Disable filter' : 'Enable filter'}
      >
        {rule.enabled ? '✓' : ''}
      </button>

      <select
        value={rule.column}
        onChange={e => onChange({ ...rule, column: e.target.value, operator: getOperatorsForType(columns.find(c => c.name === e.target.value)?.type || 'string')[0].value })}
        className="border py-1 px-1.5 min-w-0"
        style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: 'transparent', maxWidth: 130 }}
      >
        {columns.map(c => (
          <option key={c.name} value={c.name}>{c.name}</option>
        ))}
      </select>

      <select
        value={rule.operator}
        onChange={e => onChange({ ...rule, operator: e.target.value })}
        className="border py-1 px-1.5 min-w-0"
        style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: 'transparent', maxWidth: 120 }}
      >
        {operators.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>

      {!hideValue && (
        <input
          type={col?.type === 'number' ? 'text' : 'text'}
          value={rule.value}
          onChange={e => onChange({ ...rule, value: e.target.value })}
          placeholder={isBetween ? 'min, max' : 'value'}
          className="border py-1 px-1.5 min-w-0 flex-1"
          style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: 'transparent', maxWidth: 120 }}
        />
      )}

      <button
        onClick={onRemove}
        className="flex-shrink-0 text-xs opacity-50 hover:opacity-100"
        style={{ color: ui.textSecondary }}
        title="Remove filter"
      >
        ×
      </button>
    </div>
  );
}

function SortRuleRow({ rule, columns, onChange, onRemove, ui }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="flex-shrink-0" style={{ color: ui.textSecondary }}>Sort by</span>
      <select
        value={rule.column}
        onChange={e => onChange({ ...rule, column: e.target.value })}
        className="border py-1 px-1.5 min-w-0"
        style={{ borderColor: ui.borderSecondary, color: ui.text, backgroundColor: 'transparent', maxWidth: 130 }}
      >
        {columns.map(c => (
          <option key={c.name} value={c.name}>{c.name}</option>
        ))}
      </select>
      <div className="flex border" style={{ borderColor: ui.borderSecondary }}>
        <button
          onClick={() => onChange({ ...rule, direction: 'asc' })}
          className="px-2 py-1 text-xs"
          style={{
            backgroundColor: rule.direction === 'asc' ? ui.bgSecondary : 'transparent',
            color: rule.direction === 'asc' ? ui.text : ui.textSecondary,
          }}
        >
          A→Z
        </button>
        <button
          onClick={() => onChange({ ...rule, direction: 'desc' })}
          className="px-2 py-1 text-xs border-l"
          style={{
            borderColor: ui.borderSecondary,
            backgroundColor: rule.direction === 'desc' ? ui.bgSecondary : 'transparent',
            color: rule.direction === 'desc' ? ui.text : ui.textSecondary,
          }}
        >
          Z→A
        </button>
      </div>
      <button
        onClick={onRemove}
        className="flex-shrink-0 text-xs opacity-50 hover:opacity-100"
        style={{ color: ui.textSecondary }}
        title="Remove sort"
      >
        ×
      </button>
    </div>
  );
}

export default function DataFilterPanel({ derivedRowCount }) {
  const { resolved: theme } = useTheme();
  const { columns, rawData, filterRules: _fr, sortRules: _sr, setFilterRules, setSortRules } = useData();
  const filterRules = _fr || [];
  const sortRules = _sr || [];
  const ui = theme.ui || {};
  const [showSort, setShowSort] = useState(sortRules.length > 0);

  const addFilter = () => {
    const firstCol = columns[0]?.name || '';
    const colType = columns[0]?.type || 'string';
    const ops = getOperatorsForType(colType);
    setFilterRules([
      ...filterRules,
      { id: String(Date.now()), column: firstCol, operator: ops[0].value, value: '', enabled: true },
    ]);
  };

  const updateFilter = (idx, updated) => {
    const next = [...filterRules];
    next[idx] = updated;
    setFilterRules(next);
  };

  const removeFilter = (idx) => {
    setFilterRules(filterRules.filter((_, i) => i !== idx));
  };

  const addSort = () => {
    const firstCol = columns[0]?.name || '';
    setSortRules([...sortRules, { column: firstCol, direction: 'asc' }]);
    setShowSort(true);
  };

  const updateSort = (idx, updated) => {
    const next = [...sortRules];
    next[idx] = updated;
    setSortRules(next);
  };

  const removeSort = (idx) => {
    const next = sortRules.filter((_, i) => i !== idx);
    setSortRules(next);
    if (next.length === 0) setShowSort(false);
  };

  const clearAll = () => {
    setFilterRules([]);
    setSortRules([]);
    setShowSort(false);
  };

  const totalRows = rawData.length;
  const hasRules = filterRules.length > 0 || sortRules.length > 0;

  return (
    <div
      className="border-b px-4 py-3 text-xs"
      style={{ backgroundColor: ui.bgSecondary, borderBottomColor: ui.border }}
    >
      {/* Filter rules */}
      {filterRules.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {filterRules.map((rule, idx) => (
            <FilterRuleRow
              key={rule.id}
              rule={rule}
              columns={columns}
              onChange={(updated) => updateFilter(idx, updated)}
              onRemove={() => removeFilter(idx)}
              ui={ui}
            />
          ))}
        </div>
      )}

      {/* Sort rules */}
      {showSort && sortRules.length > 0 && (
        <div className="flex flex-col gap-2 mb-3">
          {sortRules.map((rule, idx) => (
            <SortRuleRow
              key={idx}
              rule={rule}
              columns={columns}
              onChange={(updated) => updateSort(idx, updated)}
              onRemove={() => removeSort(idx)}
              ui={ui}
            />
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={addFilter}
          className="text-xs border px-2 py-1 ui-hover"
          style={{ borderColor: ui.borderSecondary, color: ui.text }}
        >
          + Add filter
        </button>
        <button
          onClick={addSort}
          className="text-xs border px-2 py-1 ui-hover"
          style={{ borderColor: ui.borderSecondary, color: ui.text }}
        >
          + Add sort
        </button>
        {hasRules && (
          <button
            onClick={clearAll}
            className="text-xs px-2 py-1 ui-hover"
            style={{ color: ui.textSecondary }}
          >
            Clear all
          </button>
        )}
        <span className="ml-auto" style={{ color: ui.textSecondary }}>
          {derivedRowCount === totalRows
            ? `${totalRows} row${totalRows !== 1 ? 's' : ''}`
            : `${derivedRowCount} of ${totalRows} rows`
          }
        </span>
      </div>
    </div>
  );
}
